import React, { useMemo } from 'react';
import Drawer, { FooterSpacer } from '../Drawer';
import Button from '../Button';
import Field from '../Field';
import { regionOptions, regionLabel, timeZoneOptions, browserTimeZone } from '../../lib/intlOptions';
import { useCommittedText, blurOnEnter } from './useCommitted';
import LogoField from './LogoField';
import { SETTINGS_WORDING as W } from '../../domain/studioSettings';
import s from './settings.module.css';

// Business Settings → "Edit Business Profile".
//
// ── What MMS does ──────────────────────────────────────────────────────────
// The drawer has tabs General and Social Links. Its General tab holds, in this
// order: Logo (hint: "This logo will appear on all business reports such as
// invoices"), Studio Name (defaulted to the teacher's own name), Phone Number
// (Optional), Studio Address (Optional, a textarea), Region, Time Zone. The
// footer is [Cancel] [Save] with Save disabled until something changes.
//
// Logo is first, as MMS has it: LogoField.jsx, kept on the studio document as
// a data URI (#498, core/src/logo.rs). The Social Links tab is not here, so
// there is no tab strip of one tab.
//
// Region's hint is quoted from MMS verbatim, because it is the only place
// currency is decided: there is no currency dropdown here or in Accounts &
// Invoices.
//
// ── The footer, a knowing divergence: Done, and no Discard ─────────────────
//
// This drawer commits each field as it is left, like every panel beside it,
// and the footer holds a single [Done] that closes it, as CategoriesDrawer and
// ChargeCategoriesDrawer do for a drawer that has already saved everything.
// The top of SettingsPage.jsx has the concurrency argument. The cost is that
// there is no Discard: a field is final once left; the write is one field and
// cannot half-apply.
//
// So Escape and the scrim cannot mean "discard", and `close` below commits what
// is in hand first.

function withCurrent(options, current, label = (id) => id) {
  if (!current || options.some((o) => o.id === current)) return options;
  return [{ id: current, label: label(current) }, ...options];
}

export default function StudioProfileDrawer({ open, settings, defaultName, onClose, onChange }) {
  // Typed fields commit when they are left; selects commit on change. Each
  // emits only its own key — the provider merges it into the record it has
  // read, so a patch of one field leaves the other four alone.
  const name = useCommittedText(
    settings.name,
    (v) => onChange({ name: v }),
    // MMS defaults Studio Name to the teacher's own name. Offered, not stored:
    // see useCommitted.js on why a suggestion nobody touched is not a choice.
    { suggestion: defaultName || '' },
  );
  const phone = useCommittedText(settings.phone, (v) => onChange({ phone: v }));
  const address = useCommittedText(settings.address, (v) => onChange({ address: v }));

  // A studio with no zone gets this machine's shown rather than a blank select
  // (lib/intlOptions.js has why the domain has no default). The app also stores
  // the device's zone on load when the studio has none (`recordStudioTimeZone`
  // in data/useSettings.jsx, #620), so this fallback covers the moment before
  // that write, or a device that could not name a zone.
  const timeZone = settings.timeZone || browserTimeZone() || '';

  // A stored value the derived list does not contain — an older locale, a zone
  // this browser's ICU has dropped — gets an option of its own at the top.
  // Without it the select renders blank and the first change silently rewrites
  // the studio's region to whatever happened to be first in the list.
  // A Region's own row is spelled out (`regionLabel`), since a sign-up can
  // store a real locale the derived list has no row for (#462).
  const regions = useMemo(
    () => withCurrent(regionOptions(), settings.locale, regionLabel),
    [settings.locale],
  );
  const zones = useMemo(() => withCurrent(timeZoneOptions(), timeZone), [timeZone]);

  // Closing commits whatever a field is still holding. Blur does it for the
  // ordinary exits — Done or the scrim moves focus out first — but Escape does
  // not, and React fires no blur on unmount, so the text would be dropped
  // silently.
  const close = () => {
    [name, phone, address].forEach((f) => f.commit());
    onClose();
  };

  return (
    <Drawer
      open={open}
      onClose={close}
      title={W.profileTitle}
      width={620}
      footer={(
        <>
          <FooterSpacer />
          {/* Done, not Save: everything here is already written. */}
          <Button onClick={close}>{W.profileDone}</Button>
        </>
      )}
    >
      <div className={s.form}>
        <LogoField logo={settings.logo || null} onChange={onChange} />

        {/* Labels are tied to their control by id: a <Field> renders a <label
            htmlFor>, and without the id it labels nothing. */}
        <Field label={W.studioName} htmlFor="studio-name">
          <input id="studio-name" className={s.input} {...name.field} onKeyDown={blurOnEnter} autoFocus />
        </Field>

        <Field label={W.phoneNumber} hint={W.optional} htmlFor="studio-phone">
          <input id="studio-phone" className={s.input} {...phone.field} onKeyDown={blurOnEnter} />
        </Field>

        <Field label={W.studioAddress} hint={W.optional} htmlFor="studio-address">
          {/* No blurOnEnter: an address is several lines and Enter is one. */}
          <textarea id="studio-address" className={s.textarea} rows={3} {...address.field} />
        </Field>

        <Field
          label={W.regionFact}
          htmlFor="studio-region"
          hint={W.regionHint}
        >
          <select
            id="studio-region"
            className={s.input}
            value={settings.locale}
            onChange={(e) => onChange({ locale: e.target.value })}
          >
            {regions.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
          </select>
        </Field>

        <Field label={W.timeZoneFact} htmlFor="studio-time-zone">
          <select
            id="studio-time-zone"
            className={s.input}
            value={timeZone}
            onChange={(e) => onChange({ timeZone: e.target.value })}
          >
            {zones.map((z) => <option key={z.id} value={z.id}>{z.label}</option>)}
          </select>
        </Field>
      </div>
    </Drawer>
  );
}
